<style scoped>
.line-separator {
    content: '';
    @apply block my-2;
    @apply border-b border-dotted;
}
.tooltip {
    position: relative;
    display: inline-block;
}

/* Tooltip text */
.tooltip .tooltiptext {
    font-size: 0.5rem;
    visibility: hidden;
    width: 100px;
    background-color: black;
    color: #fff;
    text-align: center;
    padding: 5px 0;
    border-radius: 6px;

    position: absolute;
    top: -10px;
    z-index: 1;
    left: 110%;

    @apply font-normal;
}

.tooltip:hover .tooltiptext {
    visibility: visible;
}

.tooltip .tooltiptext::after {
    content: ' ';
    position: absolute;
    top: 50%;
    right: 100%;
    margin-top: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent black transparent transparent;
}

.promotion-label {
    background-color: #fff2cc;
    border-width: 1px;
    border-color: #ffbf00;
    @apply px-3 text-xs;
}
</style>

<template>
    <productInfo
        v-if="product"
        :product="product"
        :productLinks="productLinks"
        :showProdDespImg="showProdDespImg"
    >
        <div
            class="text-start w-full text-red text-sm sm:mr-3 my-2"
            v-if="maxCustOrdQty"
        >
            ({{ $t('product.max.qty.long', maxCustOrdQty) }})
        </div>

        <div class="line-separator" />
        <div
            class="flex text-sm text-gray-650 font-extrabold leading-none mt-4 mb-2"
            v-if="product.prodRefPrcAmt"
        >
            <div class="w-20 whitespace-no-wrap">
                {{ saleCatgRefPrcName }}
                :
            </div>
            <div>{{ product.prodRefPrcAmt | price }}</div>
        </div>
        <div class="flex text-lg leading-none mb-4">
            <div
                class="w-20 whitespace-no-wrap font-extrabold"
                :class="{
                    'text-red': isLessThanOrigPrcAmt,
                    'text-darkGrayishBlue': !isLessThanOrigPrcAmt
                }"
            >
                {{ saleCatgUnitPrcName }}
                :
            </div>
            <div
                class="flex flex-grow items-center font-extrabold"
                :class="{
                    'text-red': isLessThanOrigPrcAmt,
                    'text-darkGrayishBlue': !isLessThanOrigPrcAmt
                }"
            >
                <div
                    class="font-extrabold"
                    :class="{
                        'text-red': product.prodUnitPrcAmt === 0
                    }"
                >
                    {{ product.prodUnitPrcAmt | price(true) }}
                </div>
                <div
                    class="font-extrabold text-gray-300 line-through text-base ml-6"
                    v-if="isLessThanOrigPrcAmt"
                >
                    {{ product.prodOrigUnitPrcAmt | price }}
                </div>
            </div>
        </div>
        <div class="flex items-center text-lg font-semibold leading-none">
            <div class="w-20 whitespace-no-wrap">{{ $t('product.qty') }} :</div>
            <div class="flex-1">
                <quantityInput
                    v-model="quantity"
                    :max="maxQty - productCartQty"
                    class="w-full bg-white"
                />
            </div>
        </div>

        <div class="w-full mt-2">
            <div class="flex flex-wrap items-center">
                <buttonAddToCart
                    :disabled="
                        maxQtyReached ||
                            !isShoppingListOpen ||
                            product.prodUnitPrcAmt === undefined ||
                            product.prodUnitPrcAmt === null
                    "
                    class="bg-secondary mr-2"
                    :product="product"
                    :quantity="quantity"
                />
                <slot name="button"></slot>
            </div>
            <div
                v-if="
                    product.prodUnitPrcAmt !== undefined &&
                        product.prodUnitPrcAmt !== null
                "
            >
                <div
                    class="text-red text-base"
                    v-if="productActualRemainingQty <= 0"
                >
                    <template
                        v-if="
                            product.pgsProdSaleQtyInd === 'Y' &&
                                product.rmnProdQty > productActualRemainingQty
                        "
                    >
                        <div class="tooltip">
                            {{ $t('product.pgs.zero.qty') }}
                            <span class="tooltiptext">
                                {{ $t('product.pgs.zero.qty.tooltip') }}
                            </span>
                        </div>
                    </template>
                    <template v-else>
                        {{ $t('product.zero.qty') }}
                    </template>
                </div>
                <div
                    v-else-if="product.pgsProdSaleQtyInd === 'Y'"
                    style="color: orange"
                >
                    {{ $t('product.pgs.sale') }}
                </div>

                <div class="text-red text-base" v-else-if="maxQtyReached">
                    {{ $t('product.qty.reached') }}
                </div>
            </div>
        </div>
        <div class="w-full mt-2">
            <buttonProdSmplMap
                class="w-full"
                v-if="getSmplProdMapResultProd"
                :targetProduct="getSmplProdMapResultProd"
            />
        </div>
        <div
            class="promotion-label"
            v-if="crdtPntDisabled || (hasActivePrmtEvnt && prmtEvntDisabled)"
        >
            <div>
                以下推廣不適用於本產品：
            </div>
            <div v-if="crdtPntDisabled">
                - 使用或獲得T-Coins回贈
            </div>
            <div v-if="hasActivePrmtEvnt && prmtEvntDisabled">
                - 參與幸運大抽獎
            </div>
        </div>
    </productInfo>
</template>

<script>
import productInfo from '@/components/productInfo.vue';
import buttonAddToCart from '@/components/buttonAddToCart.vue';
import buttonProdSmplMap from '@/components/buttonProdSmplMap.vue';
import quantityInput from '@/components/quantityInput.vue';
import price from '@/components/priceFilter';

export default {
    components: {
        productInfo,
        buttonAddToCart,
        buttonProdSmplMap,
        quantityInput
    },
    filters: { price },
    props: {
        product: { type: Object, required: true },
        showProdDespImg: { type: Boolean, default: true }
    },
    data() {
        return { quantity: 1 };
    },
    watch: {
        product: {
            handler: function() {
                this.$data.quantity = 1;
            }
        }
    },
    computed: {
        productCartQty() {
            const p = this.$store.state.cartItems.find(
                c => c.shopListProdKey === this.product.id
            );
            return p ? p.prodOrdQty : 0;
        },
        productLinks() {
            return this.$store.getters.getProdLinksByProdKey(
                this.product.prodKey
            );
        },
        productActualRemainingQty() {
            return this.$store.getters.productActualRemainingQty(this.product);
        },
        maxCustOrdQty() {
            return this.$store.getters.getProductMaxOrdQtyPerPerson(
                this.product
            );
        },
        maxQty() {
            return this.maxCustOrdQty
                ? Math.min(this.maxCustOrdQty, this.productActualRemainingQty)
                : this.productActualRemainingQty;
        },
        maxQtyReached() {
            return (
                this.productCartQty >= this.maxQty ||
                this.productCartQty >= this.productActualRemainingQty
            );
        },
        isLessThanOrigPrcAmt() {
            return this.$store.getters.isLessThanOrigPrcAmt(this.product);
        },
        getSmplProdMapResultProd() {
            return this.$store.getters.getSmplProdMapResult(this.product);
        },
        isShoppingListOpen() {
            return this.$store.getters.isShoppingListOpen;
        },
        saleCatgRefPrcName() {
            return (
                this.product.custRefPrcName ||
                this.$store.getters.saleCatgRefPrcName(this.product)
            );
        },
        saleCatgUnitPrcName() {
            return (
                this.product.custUnitPrcName ||
                this.$store.getters.saleCatgUnitPrcName(this.product)
            );
        },
        hasActivePrmtEvnt() {
            return this.$store.getters.hasActivePrmtEvnt;
        },
        crdtPntDisabled() {
            return (
                (this.product.crdtPntEnblInd ||
                    this.$store.state.saleCatgUseTcoinMap?.[
                        this.product.saleCatgSlctCode
                    ]) !== 'Y'
            );
        },
        prmtEvntDisabled() {
            return (
                (this.product.prmtEvntEnblInd ||
                    this.$store.state.luckydraw2021IndMap?.[
                        this.product.saleCatgSlctCode
                    ]) !== 'Y'
            );
        }
    }
};
</script>
